import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import {
	getImagesFromIssue,
	Issue,
	Section,
	USER_ROLE,
} from '../../../constants';
import config from '../../../config';

import { AppContext } from '../../../App.context';

export function useGetSections(): GetSectionsResponse {
	const { graphQLClient, sw, parseGraphQLClientError } = useContext(
		AppContext,
	);
	const [sections, setSections] = useState<Array<Section>>([]);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheTime = isAdmin
		? 0
		: 1000 * 60 * (config.app?.cache?.ttl?.getAllIssues || 60);

	const { data, isPending, error } = useQuery<SectionsQuery>({
		queryKey: ['sections'],
		queryFn: async () => graphQLClient.request(sectionsQueryDocument),
		staleTime: cacheTime,
		refetchInterval: cacheTime,
		gcTime: cacheTime,
	});

	useEffect(() => {
		if (!data) return;
		if (sw) {
			const images: Set<string> = new Set();
			const issues = data.sections.reduce(
				(acc: Array<Issue>, section) => {
					const issues = section.issues;
					if (!issues) {
						return acc;
					}

					acc = acc.concat(issues);
					return acc;
				},
				[],
			);

			getImagesFromIssue(images, issues);
			if (images.size) {
				sw.postMessage({
					type: 'DOWNLOAD_BACKGROUND_IMAGES',
					payload: {
						images: [...images],
					},
				});
			}
		}
		setSections(data.sections);
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
		sections,
	};
}

interface GetSectionsResponse {
	isLoading: boolean;
	error: Error | null;
	sections: Array<Section>;
}

interface SectionsQuery {
	sections: Array<Section>;
}

export const sectionsQueryDocument = gql`
	query GetSections {
		sections {
			id
			position
			name
			issues {
				id
				position
				name
				description
				cover
				externalUrl
				price
				features
				currency
				slug
				data
				releaseDate
				updated
				isPaid
				created
				isPurchased
			}
		}
	}
`;
